import gsap from 'gsap';
import { Container, Sprite } from 'src/game/minigame/match2-timestep/odie';
import { waitFor } from 'src/game/minigame/match2-timestep/lib/util/waitFor';

const animDuration = 1.5;

export default class WaterRipples extends Container {
  private _smallRipples: Sprite[] = [];
  private _timelines: gsap.core.Timeline[] = [];
  private _width: number;
  private _height: number;

  constructor(parent: Container, width: number, height: number) {
    super();
    parent.addChild(this);
    this._width = width;
    this._height = height;
    this._createSmallRipples();
  }

  public async triggerLargeRipple() {
    const ripple = Sprite.from('fx.pond/fx.pond.water.ripple.big.png');
    ripple.anchorX = ripple.anchorY = 0.5;
    this.addChild(ripple);

    const duration = animDuration * 0.6;
    const fadeOutRatio = 0.4;
    const targetScale = (this._width * 1.26) / ripple.width;
    const startScale = targetScale * 0.5;

    await gsap
      .timeline()
      .set(ripple, { scaleX: startScale, scaleY: startScale, alpha: 1 })
      .to(ripple, {
        duration,
        scaleX: targetScale,
        scaleY: targetScale,
        ease: 'none',
      })
      .to(
        ripple,
        { duration: duration * fadeOutRatio, alpha: 0, ease: 'quad.in' },
        duration * (1 - fadeOutRatio),
      );

    this.removeChild(ripple);
    ripple.destroy();
  }

  private _createSmallRipples() {
    const amount = 3;
    const delay = (animDuration * 1000) / amount;

    for (let index = 0; index < amount; index++) {
      const sprite = Sprite.from('fx.pond/fx.pond.water.ripple.small.png');
      sprite.anchorX = sprite.anchorY = 0.5;
      sprite.alpha = 0;
      sprite.x = Math.random() * this._width - this._width * 0.5;
      sprite.y = Math.random() * this._height - this._height * 0.5;
      this._smallRipples.push(sprite);
      this.addChild(sprite);
      waitFor(index * delay).then(() => this._animateSmallRipples(sprite));
    }
  }

  private _animateSmallRipples(ripple: Sprite) {
    const duration = animDuration;
    const fadeOutRatio = 0.5;
    const targetScale = 1;
    const onRepeat = () => {
      const dx = 0.960483;
      const dy = 0.784903;
      ripple.x =
        ((ripple.x + dx * this._width) % this._width) - this._width * 0.5;
      ripple.y =
        ((ripple.y + dy * this._height) % this._height) - this._height * 0.5;
    };

    const timeline = gsap
      .timeline({ repeat: -1, onRepeat })
      .set(ripple, { scaleX: 0, scaleY: 0, alpha: 1 })
      .to(ripple, {
        duration,
        scaleX: targetScale,
        scaleY: targetScale,
        ease: 'none',
      })
      .to(
        ripple,
        { duration: duration * fadeOutRatio, alpha: 0, ease: 'quad.in' },
        duration * (1 - fadeOutRatio),
      );

    this._timelines.push(timeline);
  }

  public removeRipples() {
    this._timelines.forEach((timeline) => timeline.kill());
    this._smallRipples.forEach((ripple) => this.removeChild(ripple));
    this.removeFromParent();
    this._timelines.length = 0;
    this._smallRipples.length = 0;
  }
}
